'use client';

import Dialog from '@/components/Dialog';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import useResponsive from '@/hooks/useResponsive';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { GIFT_ACTIVITY_TYPES, type GiftActivityType } from '@/utils/gift-activity';
import { formatPriceNumberWithSymbol, isPureNumericPrice } from '@/utils/price';
import {
  CloseOutlined,
  CheckOutlined,
  LeftArrowOutlined,
  QuestionOutlined,
  RightArrowOutlined,
} from '@zeelool/icon';
import { Dialog as MuiDialog, IconButton } from '@mui/material';
import { useLocale } from 'next-intl';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ClockIcon from './assets/clock.svg';
import FreeShippingOutlinedIcon from './assets/freeShippingOutlined.svg';
import {
  createGiftDialogFlowInitialState,
  giftDialogFlowReducer,
  resolveGiftDialogLayerOpenState,
} from './flow-state';
import {
  normalizeGiftProgressPercent,
  resolveGiftRuleContent,
  resolveGiftProgressTierStates,
  sanitizeGiftHtml,
  type GiftDialogTerms,
  type GiftRuleContent,
} from './model';

export interface GiftDialogTier {
  id: string | number;
  name: string;
  thresholdAmount?: string | number;
  thresholdText?: string;
  thumbnail: string;
  largeImage?: string;
  soldOut?: boolean;
  reached?: boolean;
  current?: boolean;
}

export interface GiftDialogPurchaseGift {
  id: string | number;
  name: string;
  image: string;
  isGlass?: boolean;
  subtitle?: string;
  thresholdText?: string;
  detail?: GiftSingleDialogData;
}

export type GiftDialogActivityDisplayStyle = 'single-tier' | 'multi-tier';

export interface GiftDialogActivity {
  id: string | number;
  title: string;
  progressPercent?: number;
  ruleLabel?: string;
  terms?: GiftDialogTerms;
  tiers?: GiftDialogTier[];
  eventPeriod?: string;
  descriptionLines?: string[];
  displayStyle?: GiftDialogActivityDisplayStyle;
  activityType?: GiftActivityType;
  singleGift?: GiftDialogPurchaseGift;
  purchaseGiftTitle?: string;
  purchaseGift?: GiftDialogPurchaseGift;
}

export interface GiftRuleButtonSource {
  ruleLabel?: string;
  terms?: GiftDialogTerms;
}

export interface GiftSingleDialogData {
  id: string | number;
  name: string;
  color?: string;
  images: string[];
  isGlass?: boolean;
  sellPrice?: string | number;
  oldPrice?: string | number;
  stockText?: string;
  comeWithText?: string;
  eventPeriod?: string;
  rules?: string[];
  descriptionLines?: string[];
  rule?: GiftRuleButtonSource;
}

export interface GiftDetailsDialogProps {
  open: boolean;
  title?: string;
  activities: GiftDialogActivity[];
  onClose: () => void;
  ruleDialogTitle?: string;
  ruleDialogContent?: string;
  onRuleClick?: (activity: GiftDialogActivity) => void;
  onTierClick?: (tier: GiftDialogTier, activity: GiftDialogActivity) => void;
  onPurchaseGiftClick?: (gift: GiftDialogPurchaseGift, activity: GiftDialogActivity) => void;
}

export interface GiftSingleDialogProps {
  open: boolean;
  title?: string;
  gift: GiftSingleDialogData;
  onClose: () => void;
  onBack?: () => void;
  onRuleClick?: (gift: GiftSingleDialogData) => void;
  onImageClick?: (index: number) => void;
}

export interface GiftImagePreviewDialogProps {
  open: boolean;
  images: string[];
  thumbnails?: string[];
  initialIndex?: number;
  onClose: () => void;
}

export interface GiftDialogFlowProps extends GiftDetailsDialogProps {
  singleTitle?: string;
  onSingleBack?: (gift: GiftSingleDialogData) => void;
  onSingleRuleClick?: (gift: GiftSingleDialogData) => void;
  onSingleImageClick?: (gift: GiftSingleDialogData, index: number) => void;
  onImagePreviewOpen?: (images: string[], initialIndex: number, source: 'tier' | 'single') => void;
}

interface GiftPreviewState {
  images: string[];
  thumbnails?: string[];
  initialIndex: number;
}

const PROGRESS_TRACK_GRADIENT =
  'linear-gradient(90deg, rgba(224, 224, 224, 0) 0%, #e0e0e0 4.1183%, #e0e0e0 95.608%, rgba(255, 255, 255, 0) 100%)';
const PROGRESS_FILL_GRADIENT = 'linear-gradient(90deg, #ffffff 0%, #463ae8 30%)';
const WEB_GIFT_PROGRESS_CLASS = 'relative mx-auto mt-[8px] w-full max-w-[552px] pt-[18px]';
const WAP_GIFT_PROGRESS_CLASS = 'relative mx-auto mt-[8px] w-full max-w-[351px] pt-[18px]';

const safeImages = (images?: string[]) => (images || []).filter(Boolean);

const getTierPreviewItems = (activity: GiftDialogActivity) =>
  (activity.tiers || []).flatMap((tier) =>
    tier.largeImage
      ? [{ image: tier.largeImage, thumbnail: tier.thumbnail || tier.largeImage }]
      : [],
  );

interface GiftDialogHeaderProps {
  title?: string;
  onClose?: () => void;
  onBack?: () => void;
}

function GiftDialogHeaderWap({ title = '', onClose, onBack }: GiftDialogHeaderProps) {
  const t = useTranslations('goodsDetail');

  return (
    <div className="h-[52px] shrink-0 px-[16px]">
      <div className="relative flex h-full items-center justify-center border-b border-[#e0e0e0]">
        {onBack && (
          <button
            type="button"
            aria-label={t('freeGift.back')}
            onClick={onBack}
            className="cursor-pointer absolute left-0 top-[15px] flex h-[22px] w-[22px] items-center justify-center"
          >
            <LeftArrowOutlined className="text-[18px] text-[#282828]" />
          </button>
        )}
        <h3 className="font-bb text-[18px] font-bold leading-[22px] text-[#282828]">{title}</h3>
        {onClose && (
          <IconButton
            aria-label={t('freeGift.close')}
            onClick={onClose}
            className="!absolute right-0 top-[10px] !p-[4px]"
          >
            <CloseOutlined className="text-[22px] text-[#282828]" />
          </IconButton>
        )}
      </div>
    </div>
  );
}

function RuleButton({
  rule,
  onClick,
  className,
}: {
  rule: GiftRuleButtonSource;
  onClick?: () => void;
  className?: string;
}) {
  if (!onClick) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex shrink-0 cursor-pointer items-center gap-[4px] text-[12px] leading-[14px] text-[#282828] ${className || ''}`}
    >
      <span>{rule.terms?.label || rule.ruleLabel || ''}</span>
      <QuestionOutlined className="text-[16px]" />
    </button>
  );
}

function TierCard({
  tier,
  compact,
  onClick,
}: {
  tier: GiftDialogTier;
  compact?: boolean;
  onClick?: () => void;
}) {
  const t = useTranslations('goodsDetail');
  const imageSize = compact ? 'h-[64px] w-[64px]' : 'h-[72px] w-[72px]';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`relative flex shrink-0 rounded-[4px] border ${
        tier.current ? 'border-[#463ae8]' : 'border-[#d1d9e0]'
      } bg-[rgba(255,255,255,0.6)] text-left ${
        compact
          ? 'w-[148px] flex-row items-center gap-[6px] p-[5px]'
          : 'w-[84px] flex-col items-center gap-[8px]  py-[5px]'
      } ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      <span
        className={`relative flex shrink-0 items-center justify-center rounded-[4px] bg-[#eeeeee] ${imageSize}`}
      >
        <Image
          src={tier.thumbnail}
          alt={tier.name}
          width={compact ? 64 : 72}
          height={compact ? 64 : 72}
          className="h-full w-full object-contain"
        />
        {tier.soldOut && (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-[rgba(0,0,0,0.55)] text-center font-br text-[10px] leading-[11px] text-white">
            {t('freeGift.soldOut')}
          </span>
        )}
      </span>
      <span
        className={`font-br text-[11px] leading-[13px] text-[#0d0e0f] ${
          compact ? 'min-w-0 flex-1' : 'line-clamp-2 w-[72px] text-center'
        }`}
      >
        {tier.name}
      </span>
    </button>
  );
}

function GiftProgress({
  tiers,
  progressPercent,
  className,
}: {
  tiers: GiftDialogTier[];
  progressPercent?: number;
  className: string;
}) {
  const t = useTranslations('goodsDetail');
  const locale = useLocale();
  const currency = useStore((state) => state.currency);
  const tierStates = resolveGiftProgressTierStates(tiers);
  const normalizedProgressPercent = normalizeGiftProgressPercent(progressPercent);
  const usesLocalizedProgressCopy = locale === 'de' || locale === 'fr';

  if (tiers.length === 0) return null;

  return (
    <div className={className}>
      <div
        className="absolute left-0 right-0 top-[7px] h-[4px] rounded-[12px]"
        style={{ backgroundImage: PROGRESS_TRACK_GRADIENT }}
      />
      {normalizedProgressPercent !== undefined && (
        <div
          className="absolute left-0 top-[7px] h-[4px] rounded-[8px]"
          style={{
            width: `${normalizedProgressPercent}%`,
            backgroundImage: PROGRESS_FILL_GRADIENT,
          }}
        />
      )}
      <div
        className="absolute top-0 grid z-[1] h-[18px]"
        style={{ insetInline: 0, gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))` }}
      >
        {tierStates.map((tier) => {
          const isCurrent = tier.state === 'current';

          return (
            <span key={`${tier.id}-node`} className="flex items-center justify-center">
              <span
                data-gift-progress-state={tier.state}
                className={`flex items-center justify-center rounded-full ${
                  isCurrent ? 'h-[18px] w-[18px] bg-[#463ae8]' : 'h-[10px] w-[10px] bg-[#e0e0e0]'
                }`}
              >
                {isCurrent && <CheckOutlined className="text-[12px] text-white" />}
              </span>
            </span>
          );
        })}
      </div>
      <div
        className="grid gap-[8px]"
        style={{ gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))` }}
      >
        {tiers.map((tier) => {
          const hasNumericThreshold = isPureNumericPrice(tier.thresholdAmount);
          const thresholdAmount = hasNumericThreshold
            ? formatPriceNumberWithSymbol(
                String(tier.thresholdAmount),
                currency,
                usesLocalizedProgressCopy,
                2,
                false,
              )
            : String(tier.thresholdAmount);
          const progressAmount = usesLocalizedProgressCopy
            ? hasNumericThreshold
              ? locale === 'de'
                ? `${thresholdAmount}+ €`
                : `${thresholdAmount} €+`
              : `${thresholdAmount}+`
            : thresholdAmount;

          return (
            <div
              key={tier.id}
              className="text-center font-br text-[12px] leading-[16px] text-[#0d0e0f]"
            >
              {tier.thresholdAmount != null ? (
                <div>
                  <p>
                    {t.rich('freeGift.spendAmount', {
                      amount: progressAmount,
                      highlight: (chunks) => <span className="font-bb">{chunks}</span>,
                    })}
                  </p>
                  <p>{t('freeGift.getFreeGift')}</p>
                </div>
              ) : (
                tier.thresholdText
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SingleTierGiftBlock({
  gift,
  onClick,
}: {
  gift: GiftDialogPurchaseGift;
  onClick?: () => void;
}) {
  const imageClassName = gift.isGlass
    ? 'h-auto w-4/5 object-contain mix-blend-multiply'
    : 'h-[64px] w-[64px] object-cover';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`flex w-full items-center gap-[8px] rounded-[4px] border border-[#d1d9e0] bg-[rgba(255,255,255,0.6)] p-[6px] text-left ${
        onClick ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      <span className="flex h-[64px] w-[64px] shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-[#f2f2f2]">
        <Image src={gift.image} alt={gift.name} width={64} height={64} className={imageClassName} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-bb text-[14px] font-bold leading-[16px] text-[#0d0e0f]">
          {gift.name}
        </span>
        {(gift.thresholdText || gift.subtitle) && (
          <span className="mt-[4px] line-clamp-2 block font-br text-[12px] leading-[16px] text-[#808080]">
            {gift.thresholdText || gift.subtitle}
          </span>
        )}
      </span>
      {onClick && <RightArrowOutlined className="shrink-0 text-[16px]" />}
    </button>
  );
}

interface GiftActivityBlockProps {
  activity: GiftDialogActivity;
  compactTier: boolean;
  progressClassName: string;
  showPurchaseGiftTitle: boolean;
  onRuleClick?: (activity: GiftDialogActivity) => void;
  onTierClick?: (tier: GiftDialogTier, activity: GiftDialogActivity) => void;
  onPurchaseGiftClick?: (gift: GiftDialogPurchaseGift, activity: GiftDialogActivity) => void;
}

function ActivityBlock({
  activity,
  compactTier,
  progressClassName,
  showPurchaseGiftTitle,
  onRuleClick,
  onTierClick,
  onPurchaseGiftClick,
}: GiftActivityBlockProps) {
  const t = useTranslations('goodsDetail');
  const tiers = activity.tiers || [];
  const hasTiers = tiers.length > 0;
  const hasPurchaseGift = !!activity.purchaseGift;
  const singleGift =
    activity.displayStyle === 'single-tier'
      ? activity.singleGift || activity.purchaseGift
      : undefined;
  const activityHeading = activity.title;

  return (
    <section className="flex flex-col gap-[24px]">
      <div className="flex flex-col gap-[16px]">
        <div className="flex items-center justify-between gap-[12px]">
          <h4 className="font-bb text-[16px] font-bold leading-[20px] text-[#282828]">
            {activityHeading}
          </h4>
          <RuleButton
            rule={activity}
            onClick={activity.terms?.show === true ? () => onRuleClick?.(activity) : undefined}
          />
        </div>

        {singleGift ? (
          <SingleTierGiftBlock
            gift={singleGift}
            onClick={
              singleGift.detail ? () => onPurchaseGiftClick?.(singleGift, activity) : undefined
            }
          />
        ) : hasTiers ? (
          <div className="flex flex-col gap-[8px]">
            <div
              className="grid w-full justify-items-center"
              style={{ gridTemplateColumns: `repeat(${tiers.length}, minmax(0, 1fr))` }}
            >
              {tiers.map((tier) => (
                <TierCard
                  key={tier.id}
                  tier={tier}
                  compact={compactTier}
                  onClick={tier.largeImage ? () => onTierClick?.(tier, activity) : undefined}
                />
              ))}
            </div>
            <GiftProgress
              tiers={tiers}
              progressPercent={activity.progressPercent}
              className={progressClassName}
            />
          </div>
        ) : null}

        {activity.activityType === GIFT_ACTIVITY_TYPES.minimumPurchase && tiers.length > 1 && (
          <div className="flex flex-col gap-[6px] font-hr text-[12px] leading-[16px] text-[#808080]">
            <p className="flex items-center gap-[4px] text-[12px] leading-[14px]">
              <Image
                src={FreeShippingOutlinedIcon}
                alt=""
                width={16}
                height={16}
                className="h-[16px] w-[16px]"
              />
              <span>{t('freeGift.comeWithProduct')}</span>
            </p>
            {activity.eventPeriod && (
              <p className="flex items-center gap-[4px]">
                <Image
                  src={ClockIcon}
                  alt=""
                  width={16}
                  height={16}
                  className="h-[16px] w-[16px]"
                />
                {t('freeGift.eventPeriod', { period: activity.eventPeriod })}
              </p>
            )}
            {activity.descriptionLines?.map((line) => <p key={line}>{line}</p>)}
          </div>
        )}
      </div>

      {!singleGift && hasPurchaseGift && activity.purchaseGift && (
        <div className="flex flex-col gap-[12px]">
          {showPurchaseGiftTitle && (
            <h4 className="font-bb text-[16px] font-bold leading-[20px] text-[#282828]">
              {activity.purchaseGiftTitle || t('freeGift.purchaseTitle')}
            </h4>
          )}
          <button
            type="button"
            onClick={() => onPurchaseGiftClick?.(activity.purchaseGift, activity)}
            className="flex w-full cursor-pointer items-center gap-[8px] rounded-[4px] border border-[#d1d9e0] bg-[rgba(255,255,255,0.6)] p-[6px] text-left"
          >
            <Image
              src={activity.purchaseGift.image}
              alt={activity.purchaseGift.name}
              width={64}
              height={64}
              className="h-[64px] w-[64px] shrink-0 rounded-[4px] object-cover"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-bb text-[14px] font-bold leading-[16px] text-[#0d0e0f]">
                {activity.purchaseGift.name}
              </span>
              {activity.purchaseGift.subtitle && (
                <span className="mt-[4px] line-clamp-2 block font-br text-[12px] leading-[16px] text-[#808080]">
                  {activity.purchaseGift.subtitle}
                </span>
              )}
            </span>
            {onPurchaseGiftClick && <RightArrowOutlined className="shrink-0 text-[16px]" />}
          </button>
        </div>
      )}
    </section>
  );
}

function GiftActivityBlockWeb(
  props: Omit<
    GiftActivityBlockProps,
    'compactTier' | 'progressClassName' | 'showPurchaseGiftTitle'
  >,
) {
  return (
    <ActivityBlock
      {...props}
      compactTier
      progressClassName={WEB_GIFT_PROGRESS_CLASS}
      showPurchaseGiftTitle
    />
  );
}

function GiftActivityBlockWap(
  props: Omit<
    GiftActivityBlockProps,
    'compactTier' | 'progressClassName' | 'showPurchaseGiftTitle'
  >,
) {
  return (
    <ActivityBlock
      {...props}
      compactTier={false}
      progressClassName={WAP_GIFT_PROGRESS_CLASS}
      showPurchaseGiftTitle={false}
    />
  );
}

export function GiftDetailsContentWeb(props: GiftDetailsDialogProps) {
  const { activities, onRuleClick, onTierClick, onPurchaseGiftClick } = props;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[552px] flex-col gap-[24px]">
          {activities.map((activity) => (
            <GiftActivityBlockWeb
              key={activity.id}
              activity={activity}
              onRuleClick={onRuleClick}
              onTierClick={onTierClick}
              onPurchaseGiftClick={onPurchaseGiftClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function GiftDetailsContentWap(props: GiftDetailsDialogProps) {
  const { title, activities, onClose, onRuleClick, onTierClick, onPurchaseGiftClick } = props;
  const t = useTranslations('goodsDetail');

  return (
    <div className="flex max-h-[calc(100dvh-40px)] flex-col bg-white">
      <GiftDialogHeaderWap title={title || t('freeGift.detailsTitle')} onClose={onClose} />
      <div className="flex flex-col gap-[32px] overflow-y-auto px-[12px] pb-[40px] pt-[18px]">
        {activities.map((activity) => (
          <GiftActivityBlockWap
            key={activity.id}
            activity={activity}
            onRuleClick={onRuleClick}
            onTierClick={onTierClick}
            onPurchaseGiftClick={onPurchaseGiftClick}
          />
        ))}
      </div>
    </div>
  );
}

export function GiftDetailsDialog(props: GiftDetailsDialogProps) {
  const { open, onClose, onRuleClick, ruleDialogContent } = props;
  const t = useTranslations('goodsDetail');
  const [activeRule, setActiveRule] = useState<GiftRuleContent | null>(null);

  const handleRuleClick = (activity: GiftDialogActivity) => {
    onRuleClick?.(activity);
    setActiveRule(
      resolveGiftRuleContent(activity.terms, {
        title: '',
        content: ruleDialogContent || '',
        contentType: 'text',
      }),
    );
  };

  useEffect(() => {
    if (!open) {
      setActiveRule(null);
    }
  }, [open]);

  return (
    <>
      <WebOnly>
        <Dialog
          open={open}
          title={props.title || t('freeGift.detailsTitle')}
          showTitleDivider
          onClose={onClose}
          footer={null}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: 'min(600px, calc(100vw - 64px))',
              borderRadius: '12px',
              boxShadow: 'none',
              overflow: 'hidden',
              maxWidth: 'calc(100vw - 64px)',
              maxHeight: 'calc(100dvh - 64px)',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          contentProps={{
            sx: {
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <GiftDetailsContentWeb {...props} onRuleClick={handleRuleClick} />
        </Dialog>
      </WebOnly>
      <WapOnly>
        <Popup
          open={open}
          onClose={onClose}
          position="bottom"
          height="auto"
          round
          borderRadius={16}
          hideCloseIcon
          title={null}
          bodyClassName="!p-0"
          childrenMaxHeight="none"
        >
          <GiftDetailsContentWap {...props} onRuleClick={handleRuleClick} />
        </Popup>
      </WapOnly>
      <GiftRuleDialog
        open={open && Boolean(activeRule)}
        title={activeRule?.title}
        content={activeRule?.content}
        contentType={activeRule?.contentType}
        onClose={() => setActiveRule(null)}
      />
    </>
  );
}

function GiftTeaserStyleCarousel({
  gift,
  isWeb,
  className = '',
}: {
  gift: GiftSingleDialogData;
  isWeb?: boolean;
  className?: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const images = safeImages(gift.images);

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <Swiper
        className="h-full w-full"
        onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.activeIndex)}
      >
        {images.map((image, index) => (
          <SwiperSlide
            key={`${image}-${index}`}
            className="!flex items-center justify-center bg-auxiliary-light-gray"
          >
            <Image
              src={image}
              alt={`${gift.name}${index + 1}`}
              width={isWeb ? 400 : 375}
              height={isWeb ? 400 : 375}
              className={`h-auto object-contain ${
                gift.isGlass ? 'w-4/5 mix-blend-multiply' : 'w-full'
              }`}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <span
        className={`absolute z-2 flex items-center justify-center bg-[#999999] text-white ${
          isWeb
            ? 'bottom-[16px] left-[16px] h-[20px] w-[50px] rounded-[14px] text-[12px] leading-[14px]'
            : 'bottom-[14px] left-[14px] h-[24px] w-[66px] rounded-[16px] bg-[rgba(0,0,0,0.4)] text-[11px] leading-[13px]'
        }`}
      >
        {activeIndex + 1}/{images.length}
      </span>
    </div>
  );
}

const useGiftSingleDialogPrices = (gift: GiftSingleDialogData) => {
  const currency = useStore((state) => state.currency);
  const sellPrice =
    gift.sellPrice != null
      ? formatPriceNumberWithSymbol(String(gift.sellPrice), currency)
      : undefined;
  const oldPrice =
    gift.oldPrice != null && gift.oldPrice !== gift.sellPrice
      ? formatPriceNumberWithSymbol(String(gift.oldPrice), currency)
      : undefined;

  return { sellPrice, oldPrice };
};

export function GiftSingleDialogContentWeb({ gift, onRuleClick }: GiftSingleDialogProps) {
  const t = useTranslations('goodsDetail');
  const { sellPrice, oldPrice } = useGiftSingleDialogPrices(gift);

  return (
    <div className="flex h-[400px] max-h-full gap-[32px] overflow-hidden bg-white">
      <div className="relative shrink-0">
        <GiftTeaserStyleCarousel gift={gift} isWeb className="h-[400px] w-[400px]" />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col text-primary-text">
        <div>
          <div className="flex items-start justify-between gap-[16px]">
            <h3 className="min-w-0 font-bb text-[32px] leading-[39px]">{gift.name}</h3>
            {gift.rule && (
              <RuleButton
                rule={gift.rule}
                onClick={gift.rule.terms?.show === true ? () => onRuleClick?.(gift) : undefined}
                className="mt-[4px]"
              />
            )}
          </div>
          {gift.color && (
            <p className="mt-[8px] text-[14px] leading-[17px] text-[#808080]">
              {t('color')} {gift.color}
            </p>
          )}
          {(sellPrice || oldPrice) && (
            <div className="mt-[24px] flex items-center gap-[8px]">
              {sellPrice && <span className="font-bb text-[24px] leading-[28px]">{sellPrice}</span>}
              {oldPrice && (
                <span className="text-[14px] leading-[17px] text-[#a9a9a9] line-through">
                  {oldPrice}
                </span>
              )}
            </div>
          )}
          <div className="mt-[24px]">
            {gift.stockText && (
              <p className="mb-[8px] text-[12px] leading-[14px]">{gift.stockText}</p>
            )}
            <p className="text-[12px] leading-[14px]">{t('freeGift.comeWithProduct')}</p>
          </div>
        </div>
        {(gift.eventPeriod || gift.descriptionLines?.length) && (
          <div className="mt-[16px] flex min-h-0 flex-1 flex-col">
            <div className="border-t border-[#dce0e8]" />
            <div className="mt-[16px] min-h-0 flex-1 overflow-auto">
              {gift.eventPeriod && (
                <p className="font-bb text-[16px] leading-[19px]">
                  {t('freeGift.eventPeriod', { period: gift.eventPeriod })}
                </p>
              )}
              {gift.descriptionLines?.map((description, index) => (
                <p
                  key={`${description}-${index}`}
                  className="mt-[8px] text-[14px] leading-[18px] text-[#808080]"
                >
                  {description}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function GiftSingleDialogContentWap({
  title,
  gift,
  onClose,
  onBack,
  onRuleClick,
}: GiftSingleDialogProps) {
  const t = useTranslations('goodsDetail');
  const dialogTitle = title || t('freeGift.detailsTitle');
  const { sellPrice, oldPrice } = useGiftSingleDialogPrices(gift);

  return (
    <div className="flex h-full flex-col bg-white">
      <GiftDialogHeaderWap
        title={dialogTitle}
        onBack={onBack}
        onClose={onBack ? undefined : onClose}
      />
      <div className="min-h-0 flex-1 overflow-auto">
        <GiftTeaserStyleCarousel gift={gift} className="h-[375px] w-full" />
        <div className="px-[14px] pb-34 pt-[15px] text-primary-text">
          <div className="flex items-start justify-between gap-[12px]">
            <div className="flex min-w-0 items-center gap-[8px]">
              <h4 className="font-bb text-[14px] leading-[17px]">{gift.name}</h4>
              {gift.color && <span className="text-[14px] leading-[17px]">{gift.color}</span>}
            </div>
            {gift.rule && (
              <RuleButton
                rule={gift.rule}
                onClick={gift.rule.terms?.show === true ? () => onRuleClick?.(gift) : undefined}
              />
            )}
          </div>
          {(sellPrice || oldPrice) && (
            <div className="mt-[16px] flex items-center gap-[8px]">
              {sellPrice && <span className="font-bb text-[20px] leading-[24px]">{sellPrice}</span>}
              {oldPrice && (
                <span className="text-[14px] leading-[17px] text-[#a9a9a9] line-through">
                  {oldPrice}
                </span>
              )}
            </div>
          )}
          <div className="mt-[16px]">
            {gift.stockText && (
              <p className="mb-[6px] text-[12px] leading-[14px]">{gift.stockText}</p>
            )}
            <p className="text-[12px] leading-[14px]">{t('freeGift.comeWithProduct')}</p>
          </div>
          {(gift.eventPeriod || gift.descriptionLines?.length) && (
            <div className="mt-[16px] border-t border-[#dce0e8] border-t-[0.5px] pt-[16px]">
              {gift.eventPeriod && (
                <p className="font-bb text-[14px] leading-[17px]">
                  {t('freeGift.eventPeriod', { period: gift.eventPeriod })}
                </p>
              )}
              {gift.descriptionLines?.map((description, index) => (
                <p
                  key={`${description}-${index}`}
                  className="mt-[8px] text-[12px] leading-[16px] text-[#808080]"
                >
                  {description}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GiftTeaserStyleSingleContent(props: GiftSingleDialogProps) {
  return (
    <>
      <WebOnly>
        <GiftSingleDialogContentWeb {...props} />
      </WebOnly>
      <WapOnly>
        <GiftSingleDialogContentWap {...props} />
      </WapOnly>
    </>
  );
}

export function GiftSingleDialog(props: GiftSingleDialogProps) {
  const { open, onClose } = props;
  const t = useTranslations('goodsDetail');
  const [activeRule, setActiveRule] = useState<GiftRuleContent | null>(null);
  const handleDialogClose = props.onBack ?? onClose;
  const dialogTitle = props.title || t('freeGift.detailsTitle');
  const handleRuleClick = (gift: GiftSingleDialogData) => {
    if (props.onRuleClick) {
      props.onRuleClick(gift);
      return;
    }

    setActiveRule(
      resolveGiftRuleContent(gift.rule?.terms, {
        title: '',
        content: '',
        contentType: 'text',
      }),
    );
  };

  useEffect(() => {
    if (!open) setActiveRule(null);
  }, [open]);

  const webDialogTitle = props.onBack ? (
    <>
      <IconButton
        aria-label={t('freeGift.back')}
        onClick={props.onBack}
        sx={{
          position: 'absolute',
          left: 8,
          top: 14,
          color: 'text.primary',
        }}
      >
        <LeftArrowOutlined className="text-[24px] text-primary-black" />
      </IconButton>
      {dialogTitle}
    </>
  ) : (
    dialogTitle
  );

  return (
    <>
      <WebOnly>
        <Dialog
          open={open}
          title={webDialogTitle}
          showCloseIcon={props.onBack ? false : undefined}
          showTitleDivider
          onClose={handleDialogClose}
          footer={null}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: '864px',
              maxWidth: '864px',
              maxHeight: '80vh',
              borderRadius: '12px',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          }}
        >
          <GiftTeaserStyleSingleContent {...props} onRuleClick={handleRuleClick} />
        </Dialog>
      </WebOnly>
      <WapOnly>
        <Popup
          open={open}
          onClose={handleDialogClose}
          position="bottom"
          height="80%"
          round
          borderRadius={16}
          hideCloseIcon
          title={null}
          bodyClassName="!p-0"
          childrenMaxHeight="none"
        >
          <GiftTeaserStyleSingleContent {...props} onRuleClick={handleRuleClick} />
        </Popup>
      </WapOnly>
      <GiftRuleDialog
        open={open && Boolean(activeRule)}
        title={activeRule?.title}
        content={activeRule?.content}
        contentType={activeRule?.contentType}
        onClose={() => setActiveRule(null)}
      />
    </>
  );
}

function GiftRuleDialog({
  open,
  title,
  content = '',
  contentType = 'text',
  onClose,
}: {
  open: boolean;
  title?: string;
  content?: string;
  contentType?: 'text' | 'html';
  onClose: () => void;
}) {
  const { isMobile } = useResponsive();

  return (
    <Dialog.Info
      open={open}
      title={title ?? ''}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '360px',
          ...(isMobile
            ? {
                borderRadius: '12px',
                padding: '0 0 16px 0',
                margin: '0',
              }
            : {}),
        },
      }}
      contentProps={isMobile ? { sx: { padding: '16px 16px 0' } } : undefined}
    >
      {contentType === 'html' ? (
        <div
          className="font-br text-[14px] leading-[18px] text-[#282828]"
          dangerouslySetInnerHTML={{ __html: sanitizeGiftHtml(content) }}
        />
      ) : (
        <p className="whitespace-pre-wrap font-br text-[14px] leading-[18px] text-[#282828]">
          {content}
        </p>
      )}
    </Dialog.Info>
  );
}

export function GiftImagePreviewDialog(props: GiftImagePreviewDialogProps) {
  const { open, images, thumbnails, initialIndex = 0, onClose } = props;
  const safe = useMemo(() => safeImages(images), [images]);
  const normalizedInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(safe.length - 1, 0));

  if (!open || safe.length === 0) return null;

  return (
    <MuiDialog
      open={open}
      fullScreen
      onClose={onClose}
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100dvh',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: 0,
          overflow: 'hidden',
          borderRadius: 0,
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'transparent',
          },
        },
      }}
    >
      <GiftPreviewContent
        images={safe}
        thumbnails={thumbnails}
        initialIndex={normalizedInitialIndex}
        onClose={onClose}
      />
    </MuiDialog>
  );
}

export function GiftPreviewContent({
  images,
  thumbnails,
  initialIndex = 0,
  onClose,
}: Omit<GiftImagePreviewDialogProps, 'open'>) {
  const t = useTranslations('goodsDetail');
  const previewItems = useMemo(
    () =>
      images.flatMap((image, index) =>
        image ? [{ image, thumbnail: thumbnails?.[index] || image }] : [],
      ),
    [images, thumbnails],
  );
  const normalizedInitialIndex = Math.min(
    Math.max(initialIndex, 0),
    Math.max(previewItems.length - 1, 0),
  );
  const [activeIndex, setActiveIndex] = useState(normalizedInitialIndex);

  useEffect(() => {
    setActiveIndex(normalizedInitialIndex);
  }, [normalizedInitialIndex]);

  if (previewItems.length === 0) return null;

  const currentIndex = Math.min(activeIndex, previewItems.length - 1);
  const activeImage = previewItems[currentIndex].image;
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < previewItems.length - 1;

  return (
    <div
      data-testid="gift-preview-root"
      className="relative h-[100dvh] w-screen overflow-hidden bg-black"
    >
      <button
        type="button"
        aria-label={t('freeGift.back')}
        className="absolute left-0 top-0 z-20 flex h-[48px] w-[48px] cursor-pointer items-center justify-center text-white md:hidden"
        onClick={onClose}
      >
        <LeftArrowOutlined size={22} />
      </button>

      <div className="gift-preview-desktop-stage absolute inset-0 hidden overflow-hidden md:block">
        <button
          type="button"
          aria-label={t('freeGift.close')}
          className="gift-preview-desktop-close absolute right-[40px] top-[24px] z-20 flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-black/40 text-white"
          onClick={onClose}
        >
          <CloseOutlined size={24} />
        </button>

        <div className="gift-preview-desktop-design absolute left-1/2 top-0 h-[min(900px,100dvh)] w-[1920px] -translate-x-1/2">
          <div className="gift-preview-desktop-frame absolute left-1/2 top-0 flex aspect-square w-[min(720px,calc(100dvh_-_180px))] -translate-x-1/2 items-center justify-center bg-[#eeeeee]">
            <Image
              src={activeImage}
              alt={t('freeGift.previewAlt', { count: currentIndex + 1 })}
              width={720}
              height={720}
              className="h-full w-full object-contain"
            />

            {previewItems.length > 1 && (
              <div className="absolute left-1/2 top-1/2 flex w-[calc(100%+118px)] -translate-x-1/2 -translate-y-1/2 items-center justify-between">
                <button
                  type="button"
                  aria-label={t('freeGift.previousImage')}
                  disabled={!canGoPrevious}
                  className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
                  onClick={() => setActiveIndex((index) => Math.max(index - 1, 0))}
                >
                  <LeftArrowOutlined size={20} />
                </button>
                <button
                  type="button"
                  aria-label={t('freeGift.nextImage')}
                  disabled={!canGoNext}
                  className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
                  onClick={() =>
                    setActiveIndex((index) => Math.min(index + 1, previewItems.length - 1))
                  }
                >
                  <RightArrowOutlined size={20} />
                </button>
              </div>
            )}
          </div>

          {previewItems.length > 1 && (
            <div className="scrollbar-hide absolute left-1/2 top-[min(740px,calc(100dvh_-_160px))] flex -translate-x-1/2 items-center gap-[6px] overflow-x-auto">
              {previewItems.map(({ image, thumbnail }, index) => {
                const active = index === currentIndex;
                return (
                  <button
                    key={`desktop-${image}-${index}`}
                    type="button"
                    aria-label={t('freeGift.previewImage', { count: index + 1 })}
                    aria-current={active ? 'true' : undefined}
                    className={`flex shrink-0 cursor-pointer items-center justify-center rounded-[4px] ${
                      active ? 'h-[144px] w-[144px] border border-white' : 'h-[140px] w-[140px]'
                    }`}
                    onClick={() => setActiveIndex(index)}
                  >
                    <span className="flex h-[140px] w-[140px] items-center justify-center rounded-[4px] bg-[#eeeeee]">
                      <Image
                        src={thumbnail}
                        alt={t('freeGift.thumbnailAlt', { count: index + 1 })}
                        width={140}
                        height={140}
                        className="h-full w-full object-contain"
                      />
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div
        data-testid="gift-preview-canvas"
        className="absolute left-1/2 top-1/2 flex h-[100vw] w-[100vw] -translate-x-1/2 -translate-y-1/2 items-center justify-center bg-[#eeeeee] md:hidden"
      >
        <Image
          src={activeImage}
          alt={t('freeGift.previewAlt', { count: currentIndex + 1 })}
          width={720}
          height={720}
          className="h-full w-full object-contain"
        />

        {previewItems.length > 1 && (
          <div className="absolute left-1/2 top-1/2 hidden w-[calc(100%+118px)] -translate-x-1/2 -translate-y-1/2 items-center justify-between md:flex">
            <button
              type="button"
              aria-label={t('freeGift.previousImage')}
              disabled={!canGoPrevious}
              className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
              onClick={() => setActiveIndex((index) => Math.max(index - 1, 0))}
            >
              <LeftArrowOutlined size={20} />
            </button>
            <button
              type="button"
              aria-label={t('freeGift.nextImage')}
              disabled={!canGoNext}
              className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-black/40 text-white disabled:cursor-default disabled:opacity-40"
              onClick={() =>
                setActiveIndex((index) => Math.min(index + 1, previewItems.length - 1))
              }
            >
              <RightArrowOutlined size={20} />
            </button>
          </div>
        )}
      </div>

      {previewItems.length > 1 && (
        <div className="scrollbar-hide absolute bottom-[16px] left-1/2 flex max-w-[calc(100vw-24px)] -translate-x-1/2 items-center gap-[6px] overflow-x-auto md:hidden">
          {previewItems.map(({ image, thumbnail }, index) => {
            const active = index === currentIndex;
            return (
              <button
                key={`${image}-${index}`}
                type="button"
                data-testid="gift-preview-thumbnail"
                aria-label={t('freeGift.previewImage', { count: index + 1 })}
                aria-current={active ? 'true' : undefined}
                className={`flex shrink-0 cursor-pointer items-center justify-center rounded-[4px] ${
                  active
                    ? 'h-[76px] w-[76px] border border-white md:h-[144px] md:w-[144px]'
                    : 'h-[72px] w-[72px] md:h-[140px] md:w-[140px]'
                }`}
                onClick={() => setActiveIndex(index)}
              >
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-[4px] bg-[#eeeeee] md:h-[140px] md:w-[140px]">
                  <Image
                    src={thumbnail}
                    alt={t('freeGift.thumbnailAlt', { count: index + 1 })}
                    width={140}
                    height={140}
                    className="h-full w-full object-contain"
                  />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function GiftDialogFlow({
  open,
  onClose,
  title,
  singleTitle,
  ruleDialogTitle,
  ruleDialogContent,
  activities,
  onRuleClick,
  onTierClick,
  onPurchaseGiftClick,
  onSingleBack,
  onSingleRuleClick,
  onSingleImageClick,
  onImagePreviewOpen,
}: GiftDialogFlowProps) {
  const t = useTranslations('goodsDetail');
  const [flowState, dispatch] = useReducer(
    giftDialogFlowReducer<GiftSingleDialogData, GiftPreviewState>,
    createGiftDialogFlowInitialState<GiftSingleDialogData, GiftPreviewState>(),
  );
  const { selectedGift, preview } = flowState;
  const layerOpen = resolveGiftDialogLayerOpenState(open, flowState);

  useEffect(() => {
    if (!open) {
      dispatch({ type: 'reset' });
    }
  }, [open]);

  const openPreview = (
    images: string[],
    initialIndex: number,
    source: 'tier' | 'single',
    thumbnails?: string[],
  ) => {
    const safeItems = images.flatMap((image, index) =>
      image ? [{ image, thumbnail: thumbnails?.[index] || image }] : [],
    );
    if (safeItems.length === 0) return;

    const normalizedInitialIndex = Math.min(Math.max(initialIndex, 0), safeItems.length - 1);
    dispatch({
      type: 'open-preview',
      preview: {
        images: safeItems.map((item) => item.image),
        thumbnails: safeItems.map((item) => item.thumbnail),
        initialIndex: normalizedInitialIndex,
      },
    });
    onImagePreviewOpen?.(
      safeItems.map((item) => item.image),
      normalizedInitialIndex,
      source,
    );
  };

  const handleTierClick = (tier: GiftDialogTier, activity: GiftDialogActivity) => {
    onTierClick?.(tier, activity);

    const previewItems = getTierPreviewItems(activity);
    const initialIndex = Math.max(
      previewItems.findIndex((item) => item.image === tier.largeImage),
      0,
    );
    openPreview(
      previewItems.map((item) => item.image),
      initialIndex,
      'tier',
      previewItems.map((item) => item.thumbnail),
    );
  };

  const handlePurchaseGiftClick = (gift: GiftDialogPurchaseGift, activity: GiftDialogActivity) => {
    onPurchaseGiftClick?.(gift, activity);

    if (!gift.detail) return;

    dispatch({
      type: 'open-single',
      gift: {
        ...gift.detail,
        rule: {
          ruleLabel: activity.ruleLabel,
          terms: activity.terms,
        },
      },
    });
  };

  const handleSingleBack = () => {
    if (selectedGift) {
      onSingleBack?.(selectedGift);
    }
    dispatch({ type: 'close-single' });
  };

  const handleSingleImageClick = (index: number) => {
    if (!selectedGift) return;

    onSingleImageClick?.(selectedGift, index);
    openPreview(selectedGift.images, index, 'single');
  };

  const handlePreviewClose = () => {
    dispatch({ type: 'close-preview' });
  };

  const handleSingleRuleClick = (gift: GiftSingleDialogData) => {
    onSingleRuleClick?.(gift);
    dispatch({ type: 'open-rule' });
  };
  const selectedGiftRuleContent = selectedGift
    ? resolveGiftRuleContent(selectedGift.rule?.terms, {
        title: ruleDialogTitle || '',
        content: ruleDialogContent || '',
        contentType: 'text',
      })
    : null;

  return (
    <>
      <GiftDetailsDialog
        open={layerOpen.details}
        title={title}
        activities={activities}
        onClose={onClose}
        ruleDialogTitle={ruleDialogTitle}
        ruleDialogContent={ruleDialogContent}
        onRuleClick={onRuleClick}
        onTierClick={handleTierClick}
        onPurchaseGiftClick={handlePurchaseGiftClick}
      />

      {selectedGift && (
        <GiftSingleDialog
          open={layerOpen.single}
          title={singleTitle || t('freeGift.detailsTitle')}
          gift={selectedGift}
          onClose={onClose}
          onBack={handleSingleBack}
          onRuleClick={handleSingleRuleClick}
          onImageClick={handleSingleImageClick}
        />
      )}

      {preview && (
        <GiftImagePreviewDialog
          open={layerOpen.preview}
          images={preview.images}
          thumbnails={preview.thumbnails}
          initialIndex={preview.initialIndex}
          onClose={handlePreviewClose}
        />
      )}

      <GiftRuleDialog
        open={layerOpen.rule}
        title={selectedGiftRuleContent ? selectedGiftRuleContent.title : ruleDialogTitle}
        content={selectedGiftRuleContent ? selectedGiftRuleContent.content : ruleDialogContent}
        contentType={selectedGiftRuleContent?.contentType}
        onClose={() => dispatch({ type: 'close-rule' })}
      />
    </>
  );
}

export default GiftDetailsDialog;
export type { GiftDialogTerms } from './model';
